import { clsx } from "clsx";
import React from "react";

export function DocPage({
    title,
    description,
    children,
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <article className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
            <header className="mb-8 border-b border-neutral-border pb-6 dark:border-white/10">
                <p className="mb-2 font-display text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-gold">
                    Maeiz Workspace
                </p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-neutral-ink dark:text-white sm:text-4xl">
                    {title}
                </h1>
                {description ? (
                    <p className="mt-3 max-w-3xl text-base text-neutral-secondary dark:text-slate-300">
                        {description}
                    </p>
                ) : null}
            </header>
            <div className="space-y-10">{children}</div>
        </article>
    );
}

export function Section({
    title,
    children,
    id,
}: {
    title: string;
    children: React.ReactNode;
    id?: string;
}) {
    return (
        <section id={id} className="scroll-mt-24">
            <h2 className="mb-4 font-display text-xl font-semibold text-neutral-dark dark:text-white">
                {title}
            </h2>
            <div className="space-y-4 text-sm leading-relaxed text-neutral-secondary dark:text-slate-300">
                {children}
            </div>
        </section>
    );
}

export function Demo({
    title,
    children,
    className,
}: {
    title?: string;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className="overflow-hidden rounded-mz-xl border border-neutral-border bg-white shadow-mz-sm dark:border-white/10 dark:bg-neutral-navy">
            {title ? (
                <div className="border-b border-neutral-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-neutral-secondary dark:border-white/10 dark:text-slate-400">
                    {title}
                </div>
            ) : null}
            <div className={clsx("p-4 sm:p-6", className)}>{children}</div>
        </div>
    );
}

export function SpecTable({ rows }: { rows: Array<[string, string]> }) {
    return (
        <div className="overflow-x-auto rounded-mz-lg border border-neutral-border dark:border-white/10">
            <table className="min-w-full text-left text-sm">
                <thead className="bg-neutral-light dark:bg-white/[0.06]">
                    <tr>
                        <th className="px-3 py-2.5 font-semibold text-neutral-dark dark:text-white">
                            Token / Prop
                        </th>
                        <th className="px-3 py-2.5 font-semibold text-neutral-dark dark:text-white">
                            Value / Rule
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([k, v]) => (
                        <tr key={k} className="border-t border-neutral-border dark:border-white/10">
                            <td className="px-3 py-2.5 font-mono text-xs font-medium text-primary dark:text-gold-light">
                                {k}
                            </td>
                            <td className="px-3 py-2.5 text-neutral-secondary dark:text-slate-300">
                                {v}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function DoDont({ doItems, dontItems }: { doItems: string[]; dontItems: string[] }) {
    return (
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-mz-lg border border-success/40 bg-success/10 p-4 dark:bg-success/15">
                <p className="mb-2 font-semibold text-success-dark dark:text-success-light">Do</p>
                <ul className="list-disc space-y-1 ps-4 text-sm text-neutral-dark dark:text-slate-200">
                    {doItems.map((i) => (
                        <li key={i}>{i}</li>
                    ))}
                </ul>
            </div>
            <div className="rounded-mz-lg border border-danger/40 bg-danger/10 p-4 dark:bg-danger/15">
                <p className="mb-2 font-semibold text-danger-dark dark:text-danger-light">
                    Don&apos;t
                </p>
                <ul className="list-disc space-y-1 ps-4 text-sm text-neutral-dark dark:text-slate-200">
                    {dontItems.map((i) => (
                        <li key={i}>{i}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
